import {
  EApiType,
  ECommonDialogType,
  EMasterPropertyCodes,
  EPropertyType,
  ERuleStrategy,
  TableField,
} from '../data/Constants';
import { TreeViewItemId } from '@mui/x-tree-view';
import { current, isDraft, type Dispatch } from '@reduxjs/toolkit';
import { openDialog } from '../store/states/DialogSlice';
import { setIsDataUpdated } from '../store/states/PdmSlice';
import { GridColDef } from '@mui/x-data-grid';
import { t } from 'i18next';
import { appSettings } from '../settings';

import type {
  ICustomTreeItem,
  IFeature,
  IFeatureData,
  IFeaturesAssociations,
  ILov,
  ILovDetail,
  IMarket,
  IMarketInfo,
  IOption,
  IProcosState,
  IProperty,
  IRule,
  ISelectedTreeItem,
  IProductHierarchy,
  IMarketRow,
  IPaginatedFeatureData,
  IEffectivity,
  PaginatedState,
  IProductModelResponseData,
  PaginationAction,
  IFeatureState,
  IFeaturePayload,
} from '../../types';
import type { ISearchRequest } from '../../types/IRequestTypes';
import { AxiosResponse } from 'axios';

const { RuleStrategy } = appSettings.ProcosSaveBatching || {};

type ApiResponse = Partial<AxiosResponse> & Pick<AxiosResponse, 'data'>;

/**
 * Map the Market associations to Features object
 * @param {IMarketInfo[]} marketAssociations All Market associations
 * @param {string} productCode ProductCode
 * @param {string [] } productMarkets product markets array
 * @returns {IFeaturesAssociations} Feature Market associations
 */
export const mapMAToFeatureState = (
  marketAssociations: IMarketInfo[],
  productCode: string,
  productMarkets: string[],
  allMarkets: string[]
) => {
  const featureMarketAssociations: IFeaturesAssociations = {};
  //Filter feature Associations, then loop through it to set the Feature Associations Object
  marketAssociations
    .filter((details) => details.code !== productCode)
    .forEach((marketDetails) => {
      marketDetails.countries =
        marketDetails.includeMarkets === false
          ? flipCountries(allMarkets, marketDetails.countries)
          : marketDetails.countries;
      featureMarketAssociations[marketDetails.code] = {
        initialMarkets: marketDetails.countries,
        updatedMarkets: fetchValidMarkets(marketDetails.countries, productMarkets),
      };
    });
  return featureMarketAssociations;
};

/**
 * Returns the family type of particular feature code
 * @param {IFeatureData} features Features
 * @param {string} featureCode Feature code
 * @returns {string} feature family type
 */
export function getFeatureType(features: IFeatureData, featureCode: string): string {
  if (features.data) {
    const feature = features.data.find((ftr: IFeature) => ftr.code === featureCode);
    if (feature) {
      return feature.familyType;
    }
  }
  return '';
}
/**
 * To get the Feature Market Associations in a required Format
 * @param {IFeaturesAssociations} featuresMA Updated Market Associations of features
 * @param {IFeatureData} features features
 * @returns Feature Market Associations in a required rule Format for API call
 */

/**
 * To get the Feature Market Associations in a required Format
 * @param {IFeatureData} features features
 * @param {IFeaturesAssociations} featuresMA Updated Market Associations of features
 * @param {number} productMALength product market associations length
 * @param {string} allMarkets all available Markets
 * @returns {IRule[]} rules
 */
export const getFeatureMARulesForPayload = (
  features: IFeatureData,
  featuresMA: IFeaturesAssociations,
  productMALength: number,
  allMarkets: string[]
): IRule[] => {
  const featureMARules: IRule[] = [];
  Object.keys(featuresMA).forEach(function (key) {
    const featureCodes = key.split('.');
    const featureCode = featureCodes.length === 0 ? featureCodes[0] : featureCodes.at(-1);

    //New Association
    //Below criteria procos updates are handled
    // When initial Procos at feature/family level is present and there is a change done by user at procos other than
    // resetting to product level
    if (
      featuresMA[key].updatedMarkets.length < productMALength &&
      !isEqualAssociations(featuresMA[key].initialMarkets, featuresMA[key].updatedMarkets)
    ) {
      const featureMARule: IRule = {
        code: key,
        type: featuresMA[key].type ?? getFeatureType(features, featureCode),
        countries:
          RuleStrategy === ERuleStrategy.Exclusive
            ? flipCountries(allMarkets, featuresMA[key].updatedMarkets)
            : featuresMA[key].updatedMarkets,
      };
      if (RuleStrategy === ERuleStrategy.Exclusive) {
        featureMARule.includeMarkets = false;
      }
      featureMARules.push(featureMARule);
    } else if (
      featuresMA[key].updatedMarkets.length === productMALength &&
      featuresMA[key].initialMarkets.length > 0
    ) {
      //Delete the Rule which means send empty countries, when it needs to follow product level
      // Below condition is excecuted when market association at feature level is reset to product level
      // When feature/family does not have initial procos data
      // this is used for deleting existing procos information in ACE
      featureMARules.push({
        code: key,
        type: featuresMA[key].type ?? getFeatureType(features, featureCode),
        countries: [],
      });
    }
  });

  return featureMARules;
};

//Method returns feature effectivity
export const getFeatureEffectivityDataForPayload = (
  features: IFeatureData,
  procos: IProcosState
): IEffectivity[] => {
  const featureEffectivities: IEffectivity[] = [];
  const initialActiveStatusOfFeature = { ...procos.features.status.initial };
  const updatedActiveStatusOfFeature = { ...procos.features.status.updated };
  // when market association is not present for any feature, featureMA is not having data of feature updated

  Object.keys(initialActiveStatusOfFeature).forEach((key) => {
    const hasUpdatedStatus =
      initialActiveStatusOfFeature[key] !== updatedActiveStatusOfFeature[key];
    const featureCodes = key.split('.');
    const featureCode = featureCodes.length === 0 ? featureCodes[0] : featureCodes.at(-1);
    if (hasUpdatedStatus) {
      featureEffectivities.push({
        code: key,
        type: getFeatureType(features, featureCode),
        isActive: updatedActiveStatusOfFeature[key],
      });
    }
  });

  return featureEffectivities;
};

//Compares the given Array of countries and returns true if both are equal else false
export function isEqualAssociations(initialMarkets: string[], updatedMarkets: string[]): boolean {
  const compareFunction = (a: string, b: string) => a.localeCompare(b);

  const sortedInitialMarkets = initialMarkets.slice().sort(compareFunction);
  const sortedUpdatedMarkets = updatedMarkets.slice().sort(compareFunction);

  return (
    sortedInitialMarkets.length === sortedUpdatedMarkets.length &&
    sortedInitialMarkets.every((market, index) => market === sortedUpdatedMarkets[index])
  );
}

//To Store the data in state in a required format for page purpose
export function getPaginatedData(
  state: PaginatedState<IProductModelResponseData>,
  action: PaginationAction<IProductModelResponseData>
) {
  const newData = { ...state };
  if (action.page === 1) {
    newData['data'] = {}; // To reset data on every load of first page as change in recordLength might affect the functionality
  }
  if (state.data && Object.keys(state.data).length > 3) {
    // Delete the second entry of data element as first element is the initial load data
    if (newData?.data) {
      const keys = Object.keys(newData.data);
      const secondKey = keys[1] === undefined ? undefined : Number(keys[1]);
      if (secondKey !== undefined) {
        delete newData.data[secondKey];
      }
    }
  }
  newData['searchKey'] = action?.searchKey ?? '';
  action.data = action.data.map((item, index) => ({
    ...item,
    id: `${action.page}-${index + 1}`,
  }));
  newData['data'] = {
    ...newData['data'],
    [action.page]: action.data ?? [],
  };
  newData['page'] = action.page;
  newData['totalCount'] = action.totalRecords;
  newData['limit'] = action.limit ?? 15;
  return { ...newData };
}

//To Revert the Feature Market Associations to initial Saved selections
export function revertFeaturesMA(
  featuresMA: IFeaturesAssociations,
  productMa: string[]
): IFeaturesAssociations {
  Object.keys(featuresMA).forEach((key) => {
    if (featuresMA[key].initialMarkets.length === 0) {
      delete featuresMA[key];
    } else {
      featuresMA[key].updatedMarkets = fetchValidMarkets(featuresMA[key].initialMarkets, productMa);
    }
  });

  return featuresMA;
}

// After Save is complete and GET call of feature is made,
// we lose our previous state data for SaveStatus for each feature row. Therefore added
// additional updatedFeatureData parameter to get hold of SaveStatus.
/**
 * To Create Features in a paginated format
 * @param {IPaginatedFeatureData} featureData feature data available from API calls
 * @param {IFeatureData} updatedFeatureData is updated feature data with saveStatus
 * @returns {IFeature[]} featureRows array
 */
export function createPaginatedFeatures(
  featureData: IPaginatedFeatureData,
  updatedFeatureData: IFeatureData
): IFeature[] {
  const featureRows: IFeature[] = [];
  // Build a map for quick lookup of updated features by code
  const updatedFeatureMap = new Map<string, IFeature>();
  updatedFeatureData?.data?.forEach((f) => {
    updatedFeatureMap.set(f.code, f);
  });

  featureData.data?.forEach((feature: IFeature) => {
    const updatedStateFeatureRow = updatedFeatureMap.get(feature.code);
    if (feature.features && feature.features.length > 0) {
      feature.features.forEach((option: IOption) => {
        featureRows.push({
          ...option,
          id: feature.code + '.' + option.code,
          [TableField.feature]: feature.code + '.' + feature.description,
          [TableField.option]: option.code + '.' + option.description,
          [TableField.type]: feature.familyType,
          [TableField.saveStatus]: updatedStateFeatureRow?.saveStatus,
          familyType: feature.familyType,
        });
      });
    } else {
      featureRows.push({
        ...feature,
        id: feature.code,
        [TableField.feature]: feature.code + '.' + feature.description,
        [TableField.saveStatus]: updatedStateFeatureRow?.saveStatus,
        [TableField.type]: feature.familyType,
      });
    }
  });

  // when key = FUS8528.NUSZ890 and feature.code = NUSZ890
  // This case is true when feature has option data
  // In This scenario we check code after . notation
  // Check if this code is needed ??
  // updatedStateFeatureData?.forEach( ( updatedFeature: IFeature ) => {
  //   featureRows?.forEach( ( feature ) => {
  //     if ( feature.code === updatedFeature.code ) {
  //       feature.saveStatus = updatedFeature?.saveStatus;
  //     }
  //   }
  //   )
  // } )

  return featureRows;
}

//To remove a Market from all the Features Market association
export function removeMarketFeatureMA(
  featuresMa: IFeaturesAssociations,
  market: string,
  productMaLength: number
): IFeaturesAssociations {
  Object.keys(featuresMa).forEach((key) => {
    const index = featuresMa[key].updatedMarkets.indexOf(market);
    if (index >= 0) {
      //Market is present

      //Initial countries are not present which means no rule and this is the last element we are removing from array hence delete
      if (
        featuresMa[key].initialMarkets.length === 0 &&
        featuresMa[key].updatedMarkets.length === 1
      ) {
        delete featuresMa[key];
        return;
      }
      featuresMa[key].updatedMarkets.splice(index, 1);
    } else {
      //Market is not present
      //Initial countries are not present which means no rule and option and product selections are equal so can be deleted
      if (
        featuresMa[key].initialMarkets.length === 0 &&
        featuresMa[key].updatedMarkets.length === productMaLength
      ) {
        delete featuresMa[key];
        return;
      } //Initial countries length is greater than 0 and now following Product model selections so can be made to empty array indicating rule can be deleted
      if (
        featuresMa[key].initialMarkets.length > 0 &&
        featuresMa[key].updatedMarkets.length === productMaLength
      ) {
        featuresMa[key].updatedMarkets = [];
      }
    }
  });
  return featuresMa;
}

//To return data object on API call success
export function handleSuccess(response: ApiResponse, action?: EApiType, dispatch?: Dispatch) {
  //Specific dispatches based on api responses
  if (action === EApiType.SaveProperties) {
    dispatch?.(setIsDataUpdated(false));
    dispatch?.(
      openDialog({
        type: ECommonDialogType.Success,
        data: t('messages.submit.general'),
      })
    );
  } else if (action === EApiType.SendNotification) {
    dispatch?.(
      openDialog({
        type: ECommonDialogType.Success,
        data: t('request.success'),
      })
    );
  } else if (action === EApiType.Download) {
    let fileName = response.headers?.['content-disposition'];
    const prefixRegex =
      /^(?:inline|attachment);\s*filename\*?=UTF-8''|^(?:inline|attachment);\s*filename=/i;
    fileName =
      fileName?.replace(prefixRegex, '')?.replace(/^(['"])(.*)\1$/, '$2') ?? 'downloaded_file.xlsx';

    const url = globalThis.URL.createObjectURL(
      new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
      })
    );
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    dispatch?.(
      openDialog({
        type: ECommonDialogType.Success,
        data: t('messages.downloadMessage', { filename: `"${fileName}"` }),
      })
    );
  }
  return {
    data: response.data,
  };
}

//Add a market to all Features Market associations whenmarket added at Product level
export function addMarketFeatureMA(featuresMa: IFeaturesAssociations, market: string) {
  Object.keys(featuresMa).forEach((key) => {
    featuresMa[key].updatedMarkets.push(market);
  });
  return featuresMa;
}

//Get Feature Status from the API response
export function getFeatureStatus(featureData: IPaginatedFeatureData): Record<string, boolean> {
  const featureStatus: Record<string, boolean> = {};
  featureData?.data?.forEach((feature) => {
    featureStatus[feature.code] = feature.isActive;
    if (feature.features) {
      feature.features.forEach((option) => {
        featureStatus[feature.code + '.' + option.code] = option.isActive;
      });
    }
  });
  return featureStatus;
}

/**
 * This function maps the list of possible values available to enum type properties
 * @param {IProperty[]} properties that contains the details of each property
 * @param {IProductHierarchy[]} productHierarchy is the product Hierarchy list used lov of AGCode as doprdown list
 * @param {ILov[] } listOfValues for  properties
 * @returns propertiesData
 */

export function preparePropertiesLov(
  properties: IProperty[],
  productHierarchy: IProductHierarchy[],
  listOfValues: ILov[]
): IProperty[] {
  const propertiesData = structuredClone(isDraft(properties) ? current(properties) : properties);
  propertiesData.forEach((prop: IProperty) => {
    // Exceptional case for AGC to show drop down.
    if (prop.code === EMasterPropertyCodes.AgCode && prop.type === EPropertyType.String) {
      prop.type = EPropertyType.Enum;
      prop.selectionType = EPropertyType.Single;
      const agcLov: ILovDetail[] = productHierarchy.map((product: IProductHierarchy) => ({
        code: product.ag,
        name: product.description,
      }));
      prop.listOfValues = agcLov || [];
    }
    if (prop.type === EPropertyType.Enum && prop.values) {
      const foundLov = listOfValues.find((lov: ILov) => lov.code === prop.values);
      prop.listOfValues = foundLov ? foundLov.options : [];
      prop.default = prop.default ? JSON.stringify([prop.default.code]) : null;
    }
  });
  return propertiesData;
}

export function getPMPPropertyStatus(features: IFeature[]) {
  let isActiveProduct = false;
  features.forEach((feature: IFeature) => {
    if (feature.code === 'PMP') {
      isActiveProduct = feature.isActive;
    }
  });
  return isActiveProduct;
}

//Create Rows for Product Table
export function createRows(
  markets: IMarket[],
  page: number,
  limit: number,
  updatedMarkets?: string[]
) {
  const rows: IMarketRow[] = [];
  let count = 1;
  markets?.forEach((market: IMarket) => {
    if (count > (page - 1) * limit && count <= page * limit) {
      rows.push({
        id: market.code,
        [TableField.code]: market.code,
        [TableField.country]: market.name,
        [TableField.quote]: updatedMarkets?.includes(market.code) ? 'Y' : 'N',
      });
    }
    count++;
  });
  return rows;
}

//performs search functionality
export function getSearchData(
  initialData: ICustomTreeItem[],
  searchVal: string | null
): ICustomTreeItem[] {
  const treeViewData: ICustomTreeItem[] = [];
  if (searchVal) {
    //When search value is present
    const filteredData: ICustomTreeItem[] = [];
    initialData[0].children?.forEach((feature) => {
      //Search on options
      const optionsData = feature.children?.filter((option) =>
        option.label.toLowerCase().includes(searchVal.toLowerCase())
      );
      //Search on Features
      if (
        feature.label.toLowerCase().includes(searchVal.toLowerCase()) ||
        (optionsData && optionsData.length > 0)
      ) {
        filteredData.push({ ...feature, children: optionsData });
      }
    });
    //Update the tree view data with filtered values
    treeViewData.push({
      id: initialData[0].id,
      label: initialData[0].label,
      type: initialData[0].type,
      children: filteredData,
    });
    return treeViewData;
  } else {
    //When search value is cleared
    return initialData;
  }
}

//Returns all ids of tree items To Expand all items
export function getAllItemsWithChildrenIds(filteredData: ICustomTreeItem[]) {
  const itemIds: TreeViewItemId[] = [];
  const registerItemId = (item: ICustomTreeItem) => {
    if (item.children?.length) {
      itemIds.push(item.id);
      item.children.forEach(registerItemId);
    }
  };

  filteredData.forEach(registerItemId);
  return itemIds;
}

//Find selected item in Options
export function getSelectedOption(
  optionData: ICustomTreeItem[],
  id: string,
  productCode: string,
  featureCode: string
) {
  for (const option of optionData) {
    if (option.id === id) {
      //Selected item is option
      return {
        code: option.id.split('.')[1],
        label: option.label,
        type: option.type,
        productCode: productCode,
        featureCode: featureCode,
      };
    }
  }
  return null;
}

//Find selected item in Features for TreeView
export function getSelectedFeature(
  featureData: ICustomTreeItem[],
  id: string,
  productCode: string
) {
  for (const feature of featureData) {
    if (feature.id === id) {
      //Selected item is feature
      return {
        code: feature.id,
        label: feature.label,
        type: feature.type,
        productCode: productCode,
      };
    }
    if (feature.children) {
      const selectedOption = getSelectedOption(feature.children, id, productCode, feature.id);
      if (selectedOption) {
        return selectedOption;
      }
    }
  }
  return null;
}

//Gets the Selected Item for Tree View
export function getSelectedItem(
  filteredData: ICustomTreeItem[],
  id: string | null
): ISelectedTreeItem | null {
  if (filteredData.length === 0 || !id) {
    return null;
  } else if (id === filteredData[0].id) {
    //Selected item is product
    return {
      code: filteredData[0].id,
      label: filteredData[0].label,
      type: filteredData[0].type,
    };
  } else if (filteredData[0].children) {
    return getSelectedFeature(filteredData[0].children, id, filteredData[0].id);
  }
  return null;
}

//To show specific columns in Toggable Columns menu
export const getTogglableColumns = (cols: GridColDef[]) => {
  return cols
    .filter((column) => !column.field.includes(TableField.rfq))
    .map((column) => column.field);
};

//Checks if the given market associations are part of product markets and returns the filtered array
export function fetchValidMarkets(markets: string[], productMa: string[]) {
  const updatedMarkets: string[] = [];
  markets.forEach((market) => {
    const m = productMa?.find((x) => x === market);
    if (m) {
      updatedMarkets.push(market);
    }
  });
  if (updatedMarkets.length === 0) {
    return productMa;
  }
  return updatedMarkets;
}

export const flipCountries = (allMarkets: string[], originalMarkets: string[]): string[] => {
  return allMarkets?.filter((market) => !originalMarkets?.includes(market));
};

//returns the response from api in paginated format
export function handlePaginatedData<T>(
  response: { data: T[]; headers?: Record<string, unknown> },
  action: ISearchRequest
) {
  let total = 0;
  const raw = response.headers?.['x-pagination'];
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as { total?: number };
      total = parsed?.total ?? 0;
    } catch {
      total = 0;
    }
  }
  return {
    data: response.data || [],
    searchKey: action.searchCode,
    currentPage: action.page,
    totalRecords: total,
    recordsPerPage: action.limit,
  };
}

export function updateTableData(
  state: IFeatureState,
  action: IFeaturePayload,
  isSearch = false,
  requestPayload = {}
) {
  const dataKey = isSearch ? 'searchData' : 'data';

  // Create a new state object
  const newData = {
    ...state,
    [dataKey]: { ...state[dataKey] },
    requestPayload: { ...requestPayload },
    currentPage: action.currentPage,
    totalCount: action.totalRecords,
    recordsPerPage: action.recordsPerPage ?? 15,
    searchKey: isSearch ? action.searchKey : '',
  };

  if (action.currentPage === 1) {
    newData[dataKey] = {}; // To reset data on every load of first page
  }

  // Limiting the number of cached pages (adjust '5' as needed)
  const dataKeys = Object.keys(newData[dataKey] ?? {});
  if (
    dataKeys.length > 5 &&
    action.currentPage !== 1 &&
    !dataKeys.includes(String(action.currentPage))
  ) {
    // Delete the oldest cached page (assuming keys are page numbers)
    delete newData[dataKey][Math.min(...dataKeys.map(Number))];
  }

  newData[dataKey] = {
    ...newData[dataKey],
    [action.currentPage]: action.data ?? [],
  };

  return newData;
}

/**
 * Formats a date string (assumed to be in a format parsable by `new Date()`)
 * into a specific "MM-DD-YYYY HH:MM:SS UTC" string.
 *
 * @param {string} dateValue The input date string to be formatted.
 * @returns {string} The formatted date string in "MM-DD-YYYY HH:MM:SS UTC" format.
 * Returns an invalid date string if the input cannot be parsed.
 */

export const formatDateString = (dateValue: string) => {
  const date = new Date(dateValue);
  const month = ('0' + (date.getMonth() + 1)).slice(-2);
  const day = ('0' + date.getDate()).slice(-2);
  const year = date.getFullYear();
  const hour = ('0' + date.getHours()).slice(-2);
  const min = ('0' + date.getMinutes()).slice(-2);
  const seg = ('0' + date.getSeconds()).slice(-2);
  return month + '-' + day + '-' + year + ' ' + hour + ':' + min + ':' + seg + ' UTC';
};
